import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { queryClient as qc, statuses } from 'keys'
import { getAccuracyDistributionFetch, getAccuracyStatsByAssessmentFetch, getCorrectWordsPerMinuteDistributionFetch, getCorrectWordsPerMinuteStatsByAssessmentFetch, getFactoredAccuracyDistributionFetch, getFactoredAccuracyStatsByAssessmentFetch, getWpmDistributionFetch, getFluencyReadingDistributionFetch, getFluencyReadingStatsByAssessmentFetch, getFluencyStatsByAssessmentFetch, getFluencyStatsFetch, getRatingFetch, getScoreRatingMethodFetch } from 'store/middlewares/rating.crud'
import { useParams } from 'react-router-dom'
import { ratingHooks } from '.'
import { useDispatch } from 'react-redux'
import { actions } from 'store'
import { keyBy } from 'lodash'
import { getCurrentYearOpt } from 'utils/general'
import { convertArrayToIdsString } from 'utils/formatter'
import RatingPage from './ReportPage'
import { getAssessmentCountFetch } from 'store/middlewares/assessment.crud'

const WrapReportContext = (props) => {
    const [currentSection, setCurrentSection] = useState()
    const currentSectionRef = useRef()
    const dispatch = useDispatch()

    const [expand, setExpand] = useState(new Array(5).fill(false))
    const [expandItem, setExpandItem] = useState(0)
    const [distributionFilters, setDistributionFilters] = useState({
        school_year: [getCurrentYearOpt()]
    })

    const viewGraphBtns = ratingHooks.useViewGraphBtns()
    const viewGraphBtnKeys = keyBy(viewGraphBtns, "key")

    const [reportDisplay, setReportDisplay] = useState()
    const [viewGraph, setViewGraph] = useState({
        fluency: null,
        accuracy: null,
        factored_accuracy: null,
        correct_words_per_minute: null,
        reading_fluency: null
    })

    const params = useParams()
    const { assessment } = useSelector(state => state.assessment)
    const { testTaker } = useSelector(state => state.testTaker)

    const assessmentID = params?.assessmentID || assessment.id || props.assessmentID
    const distributionParams = useMemo(
        () => ({
            categories: assessment.reading_assessment_category,
            status: statuses.inProgress,
            sub_status: statuses.wordsComplete,
            test_taker: testTaker.test_taker_id,
            school_year: convertArrayToIdsString(distributionFilters.school_year),
        }),
        [assessment, testTaker, distributionFilters]
    )

    // בלי school_year בכוונה: קובע אם יש מספיק מבדקים כדי לאפשר את כפתןר "גרף התקדמות",
    // פתרון זמני כדי שיספור על פני כל השנים ולא רק את השנה שנבחרה בפילטר של הגרף עצמו
    // (אחרת הכפתור ננעל תמיד בתחילת שנה חדשה, גם לתלמיד עם היסטוריה רב-שנתית)
    const assessmentCountParams = useMemo(
        () => ({
            categories: assessment.reading_assessment_category,
            status: statuses.inProgress,
            sub_status: statuses.wordsComplete,
            test_taker: testTaker.test_taker_id,
        }),
        [assessment, testTaker]
    )

    const rating = useQuery({
        queryKey: [qc.rating, assessmentID],
        queryFn: async ({ signal }) => {
            const { data } = await getRatingFetch({ id: assessmentID, signal, })
            dispatch(actions.setRating(data.rating))
            return data.rating
        },
        enabled: !!assessmentID,
    })
    const completedAssessmentCount = useQuery({
        queryKey: [qc.assessmentCount, "report", assessmentID],
        queryFn: async () => {
            const { data } = await getAssessmentCountFetch(assessmentCountParams)
            return data
        },
        enabled: !!(assessmentID && assessment?.reading_assessment_category && testTaker),
    })

    const { data: accuracyStats, refetch: refetchAccuracyStats } = useQuery({
        queryKey: [qc.accuracyStatsByAssessment, viewGraph.accuracy, assessmentID],
        queryFn: async () => {
            const res = await getAccuracyStatsByAssessmentFetch({ id: assessmentID, params: viewGraphBtnKeys[viewGraph.accuracy].params })
            return res.data
        },
        enabled: !!viewGraph.accuracy
    })
    const { data: factoredAccuracyStats, refetch: refetchFactoredAccuracyStats } = useQuery({
        queryKey: [qc.factoredAccuracyStats, viewGraph.factored_accuracy, assessmentID],
        queryFn: async () => {
            const res = await getFactoredAccuracyStatsByAssessmentFetch({ id: assessmentID, params: viewGraphBtnKeys[viewGraph.factored_accuracy].params })
            return res.data
        },
        enabled: !!viewGraph.factored_accuracy
    })
    const { data: fluencyStats, refetch: refetchFluencyStats } = useQuery({
        queryKey: [qc.fluencyStats, viewGraph.fluency, assessmentID],
        queryFn: async () => {
            const res = await getFluencyStatsByAssessmentFetch({ id: assessmentID, params: viewGraphBtnKeys[viewGraph.fluency].params })
            return res.data
        },
        enabled: !!viewGraph.fluency
    })
    const { data: fluencyReadingStats, refetch: refetchFluencyReadingStats } = useQuery({
        queryKey: [qc.fluencyReadingStats, viewGraph.reading_fluency, assessmentID],
        queryFn: async () => {
            const res = await getFluencyReadingStatsByAssessmentFetch({ id: assessmentID, params: viewGraphBtnKeys[viewGraph.reading_fluency].params })
            return res.data
        },
        enabled: !!viewGraph.reading_fluency
    })
    const { data: correctWordsPerMinuteStats, refetch: refetchCorrectWordsPerMinuteStats } = useQuery({
        queryKey: [qc.correctWordsPerMinuteStats, viewGraph.correct_words_per_minute, assessmentID],
        queryFn: async () => {
            const res = await getCorrectWordsPerMinuteStatsByAssessmentFetch({ id: assessmentID, params: viewGraphBtnKeys[viewGraph.correct_words_per_minute].params })
            return res.data
        },
        enabled: !!viewGraph.correct_words_per_minute
    })
    const { data: wpmDistribution } = useQuery({
        queryKey: [qc.wpmDistribution, assessmentID, distributionFilters],
        queryFn: async () => {
            const res = await getWpmDistributionFetch({ id: assessmentID, params: distributionParams })
            return res.data
        },
        enabled: viewGraph.fluency == 'graph',
    })
    const { data: accuracyDistribution } = useQuery({
        queryKey: [qc.accuracyDistribution, assessmentID, distributionFilters],
        queryFn: async () => {
            const res = await getAccuracyDistributionFetch({ id: assessmentID, params: distributionParams })
            return res.data
        },
        enabled: viewGraph.accuracy == 'graph',
    })
    const { data: factoredAccuracyDistribution } = useQuery({
        queryKey: [qc.factoredAccuracyDistribution, assessmentID, distributionFilters],
        queryFn: async () => {
            const res = await getFactoredAccuracyDistributionFetch({ id: assessmentID, params: distributionParams })
            return res.data
        },
        enabled: viewGraph.factored_accuracy == 'graph',
    })
    const { data: fluencyReadingDistribution } = useQuery({
        queryKey: [qc.fluencyReadingDistribution, assessmentID, distributionFilters],
        queryFn: async () => {
            const res = await getFluencyReadingDistributionFetch({ id: assessmentID, params: distributionParams })
            return res.data
        },
        enabled: viewGraph.reading_fluency == 'graph'
    })
    const { data: correctWordsPerMinuteDistribution } = useQuery({
        queryKey: [qc.correctWordsPerMinuteDistribution, assessmentID, distributionFilters],
        queryFn: async () => {
            const res = await getCorrectWordsPerMinuteDistributionFetch({ id: assessmentID, params: distributionParams })
            return res.data
        },
        enabled: viewGraph.correct_words_per_minute == 'graph'
    })

    useEffect(() => {
        const handlePopState = () => {
            dispatch(actions.setUpdateAssessmentScreenViewFlag(true))
            window.removeEventListener('popstate', handlePopState)
        }

        window.addEventListener('popstate', handlePopState)

        return () => {
            setTimeout(() => {
                window.removeEventListener('popstate', handlePopState)
            }, 1000);
        }
    }, [])

    const commonState = {
        factoredAccuracyStats, refetchFactoredAccuracyStats,
        accuracyStats, refetchAccuracyStats,
        fluencyStats, refetchFluencyStats,
        fluencyReadingStats, refetchFluencyReadingStats,
        correctWordsPerMinuteStats, refetchCorrectWordsPerMinuteStats,
        distributionFilters, setDistributionFilters,
        reportDisplay,
        currentSectionRef,
        wpmDistribution,
        rating,
        accuracyDistribution,
        factoredAccuracyDistribution,
        correctWordsPerMinuteDistribution,
        fluencyReadingDistribution,
        completedAssessmentCount,
        currentSection, setCurrentSection,
        expandItem, setExpandItem,
        viewGraph, setViewGraph,
        setReportDisplay,
        expand, setExpand
    }

    return (
        <ReportContext.Provider value={commonState}>
            <RatingPage {...props} />
        </ReportContext.Provider>
    )
}

export const ReportContext = React.createContext()
export default WrapReportContext